import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import oauthService from '../services/oauthService';

/**
 * Test OAuth Integration Page
 * Demonstrates how to use custom GitLab URL with OAuth
 */
const TestOAuthPage = () => {
  const navigate = useNavigate();
  const [gitlabUrl, setGitlabUrl] = useState('https://gitlab.com');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication on mount and after result changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    const hasToken = !!token;
    setIsAuthenticated(hasToken);
    console.log('TestOAuthPage: Authentication check', { hasToken, tokenLength: token?.length });
  }, [result]); // Re-check after OAuth completes

  const handleTestOAuth = async (provider) => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      // For GitLab, pass the custom URL
      const gitlabUrlToUse = provider === 'gitlab' ? gitlabUrl : null;

      // Complete OAuth flow
      const response = await oauthService.completeOAuthFlow(provider, gitlabUrlToUse);

      setResult(response);
    } catch (err) {
      setError(err.message || 'OAuth flow failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGetRepositories = async (provider) => {
    setLoading(true);
    setResult(null);
    setError(null);

    try {
      const repos = await oauthService.getRepositories(provider);
      setResult({ repositories: repos, count: repos.length });
    } catch (err) {
      setError(err.message || 'Failed to fetch repositories');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 800, margin: '0 auto' }}>
      <Typography variant="h4" gutterBottom>
        OAuth Integration Test
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Test OAuth integration with custom GitLab URL support
      </Typography>

      {/* Authentication Warning */}
      {!isAuthenticated && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            ⚠️ You need to be logged in to test OAuth
          </Typography>
          <Typography variant="body2">
            OAuth endpoints require authentication. Please <Button
              size="small"
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
            >
              login
            </Button> first to test the OAuth flow.
          </Typography>
        </Alert>
      )}

      {/* GitLab URL Input */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            GitLab Configuration
          </Typography>
          <TextField
            fullWidth
            label="GitLab URL"
            value={gitlabUrl}
            onChange={(e) => setGitlabUrl(e.target.value)}
            placeholder="https://gitlab.yourcompany.com"
            helperText="Enter your custom GitLab URL or use default (gitlab.com)"
            sx={{ mb: 2 }}
          />
          <Typography variant="caption" color="textSecondary">
            Examples:
            <ul>
              <li>https://gitlab.com (default)</li>
              <li>https://gitlab.yourcompany.com</li>
              <li>https://git.internal.company.net</li>
            </ul>
          </Typography>
        </CardContent>
      </Card>

      {/* OAuth Actions */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Test OAuth Flow
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <Button
              variant="contained"
              onClick={() => handleTestOAuth('github')}
              disabled={loading || !isAuthenticated}
            >
              Test GitHub OAuth
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleTestOAuth('gitlab')}
              disabled={loading || !isAuthenticated}
            >
              Test GitLab OAuth
            </Button>
            <Button
              variant="contained"
              color="info"
              onClick={() => handleTestOAuth('bitbucket')}
              disabled={loading || !isAuthenticated}
            >
              Test Bitbucket OAuth
            </Button>
          </Stack>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Get Repositories (after OAuth)
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => handleGetRepositories('github')}
              disabled={loading || !isAuthenticated}
            >
              Get GitHub Repos
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => handleGetRepositories('gitlab')}
              disabled={loading || !isAuthenticated}
            >
              Get GitLab Repos
            </Button>
            <Button
              variant="outlined"
              color="info"
              onClick={() => handleGetRepositories('bitbucket')}
              disabled={loading || !isAuthenticated}
            >
              Get Bitbucket Repos
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Success Result */}
      {result && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom color="success.main">
              ✓ Success!
            </Typography>
            <Box
              component="pre"
              sx={{
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                maxHeight: 400
              }}
            >
              {JSON.stringify(result, null, 2)}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card sx={{ mt: 3, backgroundColor: '#f0f7ff' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            📝 How to Use
          </Typography>
          <Typography variant="body2" component="div">
            <ol>
              <li>
                <strong>For GitHub/Bitbucket:</strong> Just click the test button
              </li>
              <li>
                <strong>For Custom GitLab:</strong>
                <ul>
                  <li>Enter your GitLab URL in the field above</li>
                  <li>Click "Test GitLab OAuth"</li>
                  <li>A popup will open to your custom GitLab instance</li>
                  <li>Authorize the application</li>
                  <li>You'll see the success result with your credentials</li>
                </ul>
              </li>
              <li>
                <strong>Get Repositories:</strong> After successful OAuth, click "Get Repos" to see your repositories
              </li>
            </ol>
          </Typography>
        </CardContent>
      </Card>

      {/* API Example */}
      <Card sx={{ mt: 3, backgroundColor: '#fff8e1' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            💻 Code Example
          </Typography>
          <Box
            component="pre"
            sx={{
              backgroundColor: '#263238',
              color: '#aed581',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              fontSize: '0.875rem'
            }}
          >
{`// Import the OAuth service
import oauthService from './services/oauthService';

// For custom GitLab instance
const gitlabUrl = 'https://gitlab.yourcompany.com';

try {
  // Complete OAuth flow with custom GitLab URL
  const result = await oauthService.completeOAuthFlow(
    'gitlab',
    gitlabUrl  // Pass custom URL as second parameter
  );

  console.log('OAuth Success:', result);
  // { success: true, credential_id: "...", username: "..." }

  // Get repositories
  const repos = await oauthService.getRepositories('gitlab');
  console.log('Repositories:', repos);

} catch (error) {
  console.error('OAuth Error:', error);
}`}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TestOAuthPage;
